import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveChatArtifact } from '../lib/api/chatArtifactApi.connect';
import { collabClient } from '../lib/connect/clients';
/**
 * Save a chat artifact over Connect (sabaody -> tesoro).
 *
 * This is CREATE-OR-APPEND, not a plain update: an unknown document id creates
 * the document, a known one appends a version. That mirrors the express handler
 * it replaces, which silently doubled as create, update and version-append. It
 * matters because the agent's document tools are in-memory, so an AI-authored
 * artifact only becomes durable when a human saves it through this call.
 */ export const useUpdateDocument = ()=>{
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ documentId, payload })=>saveChatArtifact(documentId, payload),
        onSuccess: (result, variables)=>{
            // Invalidate the document query to trigger a refetch with fresh data
            queryClient.invalidateQueries({
                queryKey: [
                    'document',
                    variables.documentId
                ]
            });
            // Record the artifact into its conversation, so its card survives a page
            // reload: LogArtifactToChat appends a `data-artifact` part to the chat
            // history, and ChatView reads those parts back into the artifact store on
            // mount.
            //
            // ONLY on create, not on every save. The card represents "this document
            // exists in this chat" — one is enough, and clicking it fetches the latest
            // content anyway. Logging on every append instead appends a duplicate card
            // AND a "User saved document…" line per save, so ten edits left ten cards
            // in the thread. It also meant editing a document from the Artifacts tab
            // (an append against its old chat id) injected messages into a conversation
            // the user wasn't even looking at. Gating on 'create' fixes both.
            const { chatId } = variables.payload;
            if (!chatId || result.changeType !== 'create') return;
            // Truly fire-and-forget: NOT awaited. React Query awaits onSuccess before
            // marking the mutation resolved, so awaiting this call would keep the Save
            // button disabled on "…" for the whole log round-trip — even though the
            // document was already durably saved. The log is best-effort; a failure is
            // logged, never surfaced as a save error.
            collabClient.logArtifactToChat({
                chatId,
                documentId: variables.documentId,
                title: variables.payload.title ?? '',
                kind: variables.payload.kind ?? 'text',
                // Reference only — NOT the content. The card fetches the latest content
                // by document id when it renders, so embedding it here would write a
                // copy (up to the 10MB save ceiling) into the conversation's jsonb that
                // every chat load then re-downloads, and it would be a stale snapshot
                // the moment the document is edited. The id/title/kind are all history
                // needs to render the card and reopen the drawer.
                content: '',
                versionCreatedAt: result.document.updatedAt || new Date().toISOString()
            }).catch((error)=>{
                console.error('Artifact saved, but logging it to the chat failed:', error);
            });
        }
    });
};
